import { __assign } from 'tslib';
import React__default, { Fragment } from 'react';
import { styled, Box, Typography, Hidden } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { EditorType } from '../base-component-type.js';
import SanitizeHtml from '../SanitizeHtml/index.js';
import { PanelPosition } from './types.js';

var PREFIX = "LandingPageLayout";
var classes = {
    panel: "".concat(PREFIX, "-panel"),
    panelTop: "".concat(PREFIX, "-panelTop"),
    spaced: "".concat(PREFIX, "-spaced"),
    subtitle: "".concat(PREFIX, "-subtitle"),
    panelBottom: "".concat(PREFIX, "-panelBottom"),
    image: "".concat(PREFIX, "-image"),
    videoContainer: "".concat(PREFIX, "-videoContainer"),
    videoContainerBackground: "".concat(PREFIX, "-videoContainer-background")
};
var getRootStyles = function (_a) {
    var backgroundSrc = _a.backgroundSrc, _b = _a.landingConfiguration, fullHeightPanel = _b.fullHeightPanel, panelPosition = _b.panelPosition, theme = _a.theme;
    var style = {
        minHeight: "100vh",
        display: "flex"
    };
    // The image has to be in background if the panel is center horizontally
    // or doesn't occupy the full height
    if ((!fullHeightPanel && fullHeightPanel !== undefined) ||
        panelPosition === PanelPosition.Center) {
        style = __assign(__assign({}, style), { alignItems: !fullHeightPanel ? "center" : "initial", backgroundImage: "url(".concat(backgroundSrc, ")"), backgroundSize: "cover", backgroundPosition: "50% 50%" });
    }
    // Set the panel to its correct position
    switch (panelPosition) {
        case PanelPosition.Center:
            style = __assign(__assign({}, style), { justifyContent: "center", "& .LandingPageLayout-panel": {
                    width: theme.spacing(75)
                } });
            break;
        case PanelPosition.Right:
            style = __assign(__assign({}, style), { flexDirection: "row-reverse" });
            break;
    }
    return style;
};
var StyledLandingPageLayoutWrapper = styled(Box)(function (_a) {
    var _b, _c, _d, _e;
    var _f, _g;
    var theme = _a.theme, backgroundSrc = _a.backgroundSrc, landingConfiguration = _a.landingConfiguration;
    return (__assign(__assign({}, getRootStyles({ backgroundSrc: backgroundSrc, landingConfiguration: landingConfiguration, theme: theme })), (_b = {}, _b[theme.breakpoints.down("sm")] = {
        flexDirection: "column"
    }, _b["& .".concat(classes.panel)] = (_c = {
            height: "auto",
            width: landingConfiguration.panelWidth || theme.spacing(100),
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            backgroundColor: (_g = (_f = theme === null || theme === void 0 ? void 0 : theme.palette) === null || _f === void 0 ? void 0 : _f.common) === null || _g === void 0 ? void 0 : _g.white,
            position: "relative",
            zIndex: 1
        },
        _c[theme.breakpoints.down("sm")] = {
            width: "100%"
        },
        _c), _b["& .".concat(classes.spaced)] = {
        paddingRight: theme.spacing(4),
        paddingLeft: theme.spacing(4),
        paddingTop: theme.spacing(2.5),
        paddingBottom: theme.spacing(2.5)
    }, _b["& .".concat(classes.subtitle)] = {
        fontSize: "1.3rem",
        lineHeight: "1.5rem",
        marginTop: theme.spacing(2)
    }, _b["& .".concat(classes.videoContainer)] = (_d = {
            display: "flex",
            justifyContent: "center",
            width: "100%",
            position: "relative",
            flexGrow: 1
        },
        _d[theme.breakpoints.down("xl")] = {
            width: "auto"
        },
        _d[theme.breakpoints.down("md")] = {
            maxHeight: "50vh"
        },
        _d["& video"] = (_e = {
                height: "100%",
                width: "100%",
                objectFit: "cover",
                position: "relative"
            },
            _e[theme.breakpoints.down("md")] = {
                height: "auto"
            },
            _e),
        _d), _b["& .".concat(classes.videoContainerBackground)] = {
        position: "fixed",
        right: 0,
        bottom: 0,
        minWidth: "100%",
        minHeight: "100%"
    }, _b["& .".concat(classes.image)] = {
        height: "auto",
        width: "100%",
        backgroundSize: "cover",
        backgroundPosition: "50% 50%",
        backgroundImage: "url(".concat(backgroundSrc, ")"),
        position: "relative",
        overflow: "hidden"
    }, _b)));
});
var LandingPageLayout = function (_a) {
    var header = _a.header, mainAuthMethod = _a.mainAuthMethod, bottomComponents = _a.bottomComponents, backgroundSrc = _a.backgroundSrc, backgroundVideo = _a.backgroundVideo, logoLoginSrc = _a.logoLoginSrc, className = _a.className, subtitle = _a.subtitle, imageComponent = _a.imageComponent, loadingOptimImage = _a.loadingOptimImage, panelPosition = _a.panelPosition, panelWidth = _a.panelWidth, EditableSanitizeHtml = _a.EditableSanitizeHtml;
    return (React__default.createElement(LandingPageLayoutWrapper, { header: header, mainAuthMethod: mainAuthMethod, bottomComponents: bottomComponents, landingConfiguration: { panelPosition: panelPosition, panelWidth: panelWidth }, backgroundSrc: backgroundSrc, logoLoginSrc: logoLoginSrc, className: className, subtitle: subtitle, imageComponent: imageComponent, loadingOptimImage: loadingOptimImage, panelPosition: panelPosition, EditableSanitizeHtml: EditableSanitizeHtml, backgroundVideo: backgroundVideo }));
};
var LandingPageLayoutWrapper = function (_a) {
    var _b;
    var header = _a.header, mainAuthMethod = _a.mainAuthMethod, bottomComponents = _a.bottomComponents, landingConfiguration = _a.landingConfiguration, backgroundSrc = _a.backgroundSrc, backgroundVideo = _a.backgroundVideo, className = _a.className, subtitle = _a.subtitle, imageComponent = _a.imageComponent, loadingOptimImage = _a.loadingOptimImage, EditableSanitizeHtml = _a.EditableSanitizeHtml;
    var t = useTranslation("refront-components").t;
    var backgroundVideoSrc = (_b = backgroundVideo === null || backgroundVideo === void 0 ? void 0 : backgroundVideo.resource) === null || _b === void 0 ? void 0 : _b.url;
    var BLANK_URL = "about:blank";
    var isImageBackground = backgroundVideoSrc === undefined ||
        backgroundVideoSrc === "" ||
        backgroundVideoSrc === BLANK_URL;
    var isVideoBackground = backgroundVideoSrc !== undefined &&
        backgroundVideoSrc !== "" &&
        backgroundVideoSrc !== BLANK_URL;
    return (React__default.createElement(StyledLandingPageLayoutWrapper, { "data-testid": "StyledLandingPageLayout", className: clsx("LandingPageLayout", className), landingConfiguration: landingConfiguration, backgroundSrc: loadingOptimImage || (imageComponent && imageComponent.active) ? "" : backgroundSrc, component: "main" },
        landingConfiguration.panelPosition === PanelPosition.Center &&
            isImageBackground &&
            !loadingOptimImage &&
            imageComponent &&
            imageComponent.active &&
            imageComponent.component(backgroundSrc),
        landingConfiguration.panelPosition === PanelPosition.Center && isVideoBackground && (React__default.createElement("video", { onContextMenu: function (e) { return e.preventDefault(); }, className: classes.videoContainerBackground, key: "".concat(backgroundVideoSrc), autoPlay: backgroundVideo === null || backgroundVideo === void 0 ? void 0 : backgroundVideo.autoPlay, muted: true, loop: backgroundVideo === null || backgroundVideo === void 0 ? void 0 : backgroundVideo.loop, playsInline: true, controls: backgroundVideo === null || backgroundVideo === void 0 ? void 0 : backgroundVideo.controls },
            React__default.createElement("source", { src: backgroundVideoSrc, type: "video/mp4" }))),
        React__default.createElement(Box, { className: clsx("LandingPageLayout-panel", classes.panel), style: {
                position: "relative"
            } },
            React__default.createElement(Box, { className: clsx("LandingPageLayout-panelTop", classes.panelTop) },
                React__default.createElement(Box, { className: classes.spaced }, header),
                React__default.createElement(Box, { className: clsx("LandingPageLayout-subtitleContainer", classes.spaced) },
                    React__default.createElement(Typography, { className: clsx("LandingPageLayout-subtitle", classes.subtitle), component: "div", variant: "subtitle1", "data-testid": "subtitle" },
                        subtitle && subtitle !== "" && React__default.createElement(SanitizeHtml, { html: subtitle }),
                        (!subtitle || subtitle === "") && EditableSanitizeHtml && (React__default.createElement(EditableSanitizeHtml, { labelKey: "components.landing.page.subtitle", namespace: "refront-components", editorType: EditorType.Wyziwyg })),
                        (!subtitle || subtitle === "") && !EditableSanitizeHtml && (React__default.createElement(SanitizeHtml, { html: t("components.landing.page.subtitle") })))),
                React__default.createElement(Box, null, mainAuthMethod)),
            React__default.createElement(Box, { className: clsx("LandingPageLayout-panelBottom", classes.panelBottom) }, bottomComponents.map(function (bottomComponent, index) { return (React__default.createElement(Fragment, { key: index }, bottomComponent)); }))),
        landingConfiguration.panelPosition !== PanelPosition.Center &&
            (backgroundVideoSrc === undefined ||
                backgroundVideoSrc === "" ||
                backgroundVideoSrc === BLANK_URL) && (React__default.createElement(Hidden, { xsDown: true },
            React__default.createElement(Box, { onContextMenu: function (e) { return e.preventDefault(); }, className: classes.image }, !loadingOptimImage &&
                imageComponent &&
                imageComponent.active &&
                imageComponent.component(backgroundSrc)))),
        landingConfiguration.panelPosition !== PanelPosition.Center &&
            backgroundVideoSrc !== undefined &&
            backgroundVideoSrc !== "" &&
            backgroundVideoSrc !== BLANK_URL && (React__default.createElement(Box, { className: classes.videoContainer },
            React__default.createElement("video", { onContextMenu: function (e) { return e.preventDefault(); }, key: "".concat(backgroundVideoSrc), autoPlay: backgroundVideo === null || backgroundVideo === void 0 ? void 0 : backgroundVideo.autoPlay, muted: true, loop: backgroundVideo === null || backgroundVideo === void 0 ? void 0 : backgroundVideo.loop, playsInline: true, controls: backgroundVideo === null || backgroundVideo === void 0 ? void 0 : backgroundVideo.controls },
                React__default.createElement("source", { src: backgroundVideoSrc, type: "video/mp4" }))))));
};

export { classes, LandingPageLayout as default };
//# sourceMappingURL=index.js.map
